// Shared helpers for turning the gm-hud-config `users` map into display names. Used by the
// report flow's GM/host pickers and the checklist's "who did it" picker.

// nameForUser derives a single user's display name: an explicit `name` if set, else the
// capitalised email local part (e.g. "andreas@…" -> "Andreas").
export function nameForUser(email, user) {
    if (user?.name) return user.name;
    const local = (email || "").split("@")[0];
    return local ? local.charAt(0).toUpperCase() + local.slice(1) : email;
}

// gmNamesFromConfig is the sorted, de-duplicated picklist of selectable people. Users flagged
// `hidden` (the sheet's "Skjul i lister" column) are excluded so they can't be picked as having done a task.
export function gmNamesFromConfig(cfg) {
    const users = cfg?.users || {};
    const names = Object.entries(users)
        .filter(([, u]) => !u?.hidden)
        .map(([email, u]) => nameForUser(email, u));
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, "da"));
}

// ownNameFromConfig is the current user's own display name, for defaulting a "who did it"
// picker to the logged-in GM.
export function ownNameFromConfig(cfg) {
    const email = localStorage.getItem("userEmail") || "";
    return nameForUser(email, cfg?.users?.[email]);
}

// gmPeopleFromConfig is the sorted, de-duplicated picklist of selectable people as objects
// { name, employeeId }, so a completion can be stamped with the picked person's employee ID.
// Hidden users (the sheet's "Skjul i lister" column) are excluded.
export function gmPeopleFromConfig(cfg) {
    const users = cfg?.users || {};
    const seen = new Set();
    const people = [];
    for (const [email, u] of Object.entries(users)) {
        if (u?.hidden) continue;
        const name = nameForUser(email, u);
        if (seen.has(name)) continue;
        seen.add(name);
        people.push({ name, employeeId: u?.employeeId || "" });
    }
    return people.sort((a, b) => a.name.localeCompare(b.name, "da"));
}

// ownPersonFromConfig is the logged-in user as { name, employeeId }.
export function ownPersonFromConfig(cfg) {
    const email = localStorage.getItem("userEmail") || "";
    const u = cfg?.users?.[email];
    return { name: nameForUser(email, u), employeeId: u?.employeeId || "" };
}
